import React, { useEffect, useState } from "react";
import { Stack, Text, CommandBarButton, IButtonStyles, useTheme } from "@fluentui/react";
import { mergeStyles, mergeStyleSets } from "@fluentui/merge-styles";
import DaneValidationResultTable, { IDaneValidationTableItem } from "./DaneValidationResultTable";
import { Utils } from "../../Utils";
import { Helpers } from "./helpers/Helpers";
import DaneValidationResultTestLabels from "./DaneValidationResultTestLabels";
import { DNS_BASELINE_TEST, IDaneValidationTestType } from "../../constants/RcaDaneValidationTestTypes";
import { IRcaDaneValidationDnsResponse } from "../../models/IRcaDaneValidationDnsResponse";
import { IRcaDaneValidationMxRecordDetails } from "../../models/IRcaDaneValidationMxRecordDetails";
import { IRcaDaneValidationTlsaDetails } from "../../models/IRcaDaneValidationTlsaDetails";
import { DnsSecStatus } from "../../models/IRcaDaneValidationDnsSecResult";
import { ComponentDecorators } from "../ComponentDecorators";
import { RcaTestValidationStatus } from "../../models/RcaTestValidationStatus";

const collapseTransitionDuration = 230;

const commandBarButtonStyle: IButtonStyles = {
  root: {
    marginTop: 6,
    display: "flex",
    minWidth: 360,
    alignItems: "center",
    paddingBottom: 10,
    paddingTop: 5,
    marginRight: 15,
    fontWeight: "bold",
    cursor: "pointer",
    backgroundColor: "inherit",
  },
  rootHovered: {
    backgroundColor: "inherit",
  },
  rootPressed: {
    backgroundColor: "inherit",
  },
  textContainer: {
    fontSize: 18,
  },
};

enum AnimationState {
  COLLAPSED,
  COLLAPSING,
  EXPANDING,
  EXPANDED,
}

/**
 * The domain result UI
 *
 * @param props - The component properties
 * @returns The markup
 */
const DaneValidationResultMxEntity: React.FC<{
  /**
   * Response that contains record to be rendered
   */
  response: IRcaDaneValidationDnsResponse<IRcaDaneValidationMxRecordDetails>;
  /**
   * List of items to render in the table
   */
  record: IRcaDaneValidationMxRecordDetails;
  /**
   * Test Type of the result
   */
  testType: IDaneValidationTestType;
}> = ({ response, record, testType }) => {
  const theme = useTheme();
  const classNames = mergeStyleSets({
    wrapper: {
      height: "auto",
      overflow: "hidden",
    },
    resultTable: {
      transition: `${collapseTransitionDuration}ms`,
    },
    resultTableExpanded: {
      height: "fit-content",
    },
    resultTableCollapsed: {
      maxHeight: 0,
    },
    domainTitleWrapper: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      selectors: {
        [Utils.mobileQuery]: {
          flexDirection: "column",
          alignItems: "flex-start",
        },
      },
      paddingBottom: 6,
      backgroundColor: `var(--rca-control-background, ${theme.semanticColors.bodyBackground})`,
      ":hover": {
        backgroundColor: theme.semanticColors.bodyBackgroundHovered,
      },
    },
  });

  /**
   * Statefull method for collapsing button with animation
   *
   * @param time Time in ms to perform collapsing/expanding
   * @returns current state and functions to update it
   */
  function useDelayedCollapsing(time: number): [AnimationState, () => void, () => void] {
    const [state, setState] = useState(AnimationState.COLLAPSED);
    const show = () => {
      if (state === AnimationState.COLLAPSING) {
        return;
      }
      setState(AnimationState.EXPANDING);
    };
    const hide = () => {
      if (state === AnimationState.EXPANDING) {
        return;
      }
      setState(AnimationState.COLLAPSING);
    };

    useEffect(() => {
      let timeoutId: NodeJS.Timeout;
      if (state === AnimationState.COLLAPSING) {
        timeoutId = setTimeout(() => {
          setState(AnimationState.COLLAPSED);
        }, time);
      } else if (state === AnimationState.EXPANDING) {
        timeoutId = setTimeout(() => {
          setState(AnimationState.EXPANDED);
        }, time);
      }
      return () => {
        clearTimeout(timeoutId);
      };
    }, [state, time]);

    return [state, show, hide];
  }

  const [state, show, hide] = useDelayedCollapsing(collapseTransitionDuration);
  const [items, setItems] = useState<IDaneValidationTableItem[]>([]);

  const NA = "—"; //String(ClientStrings.DaneValidationNA);
  const A_RECORD = String(ClientStrings.DaneValidationARecord);
  const AAAA_RECORD = String(ClientStrings.DaneValidationAAAARecord);
  const TLSA_RECORD = String(ClientStrings.DaneValidationTLSARecord);
  const NO_RECORD_FOUND = String(ClientStrings.DaneValidationNoRecordFound);

  useEffect(() => {
    const items: IDaneValidationTableItem[] = [];

    // push AAAA records to list first
    if (
      record.AAAARecordsResponse.DnsSecStatus.Status === DnsSecStatus.Failure ||
      record.AAAARecordsResponse.DnsSecStatus.Status === DnsSecStatus.FailureDnsSec
    ) {
      items.push({
        RecordType: AAAA_RECORD,
        Address: WebsiteStrings.DaneErrorAaaa,
        DnsSec: record.AAAARecordsResponse.DnsSecStatus,
        Tlsa: NA,
        Dane: NA,
        Details: ComponentDecorators.replaceLinks(record.AAAARecordsResponse.DnsSecStatus.Details),
      });
    } else if (record.AAAARecordsResponse.Records.length === 0) {
      items.push({
        RecordType: AAAA_RECORD,
        Address: NO_RECORD_FOUND,
        DnsSec: NA,
        Tlsa: NA,
        Dane: NA,
        Details: ComponentDecorators.replaceLinks(record.AAAARecordsResponse.DnsSecStatus.Details),
      });
    } else {
      record.AAAARecordsResponse.Records.forEach((a) => {
        items.push({
          RecordType: AAAA_RECORD,
          Address: a.Address,
          DnsSec: record.AAAARecordsResponse.DnsSecStatus,
          Tlsa: NA,
          Dane: NA,
          Details:
            testType === DNS_BASELINE_TEST && record.AAAARecordsResponse.DnsSecStatus.Status === DnsSecStatus.Disabled
              ? ""
              : ComponentDecorators.replaceLinks(record.AAAARecordsResponse.DnsSecStatus.Details),
        });
      });
    }

    // push A records to list
    if (
      record.ARecordsResponse.DnsSecStatus.Status === DnsSecStatus.Failure ||
      record.ARecordsResponse.DnsSecStatus.Status === DnsSecStatus.FailureDnsSec
    ) {
      items.push({
        RecordType: A_RECORD,
        Address: WebsiteStrings.DaneErrorA,
        DnsSec: record.ARecordsResponse.DnsSecStatus,
        Tlsa: NA,
        Dane: NA,
        Details: ComponentDecorators.replaceLinks(record.ARecordsResponse.DnsSecStatus.Details),
      });
    } else if (record.ARecordsResponse.Records.length === 0) {
      items.push({
        RecordType: A_RECORD,
        Address: NO_RECORD_FOUND,
        DnsSec: NA,
        Tlsa: NA,
        Dane: NA,
        Details: ComponentDecorators.replaceLinks(record.ARecordsResponse.DnsSecStatus.Details),
      });
    } else {
      record.ARecordsResponse.Records.forEach((a) => {
        items.push({
          RecordType: A_RECORD,
          Address: a.Address,
          DnsSec: record.ARecordsResponse.DnsSecStatus,
          Tlsa: NA,
          Dane: NA,
          Details:
            testType === DNS_BASELINE_TEST && record.ARecordsResponse.DnsSecStatus.Status === DnsSecStatus.Disabled
              ? ""
              : ComponentDecorators.replaceLinks(record.ARecordsResponse.DnsSecStatus.Details),
        });
      });
    }

    // push TLSA records to list
    if (
      record.TlsaRecordsResponse.DnsSecStatus.Status === DnsSecStatus.Failure ||
      record.TlsaRecordsResponse.DnsSecStatus.Status === DnsSecStatus.FailureDnsSec
    ) {
      items.push({
        RecordType: TLSA_RECORD,
        Address: WebsiteStrings.DaneErrorTlsa,
        DnsSec: record.TlsaRecordsResponse.DnsSecStatus,
        Tlsa: NA,
        Dane: NA,
        Details: ComponentDecorators.replaceLinks(record.TlsaRecordsResponse.DnsSecStatus.Details),
      });
    } else if (record.TlsaRecordsResponse.Records.length === 0) {
      items.push({
        RecordType: TLSA_RECORD,
        Address: NO_RECORD_FOUND,
        DnsSec: NA,
        Tlsa: null,
        Dane: { Status: RcaTestValidationStatus.Warning, Details: WebsiteStrings.DaneValidationNotPerformed },
        Details: ComponentDecorators.replaceLinks(record.TlsaRecordsResponse.DnsSecStatus.Details),
      });
    } else {
      record.TlsaRecordsResponse.Records.forEach((tlsa: IRcaDaneValidationTlsaDetails) => {
        items.push({
          RecordType: TLSA_RECORD,
          Address: tlsa.TlsaRecordData,
          DnsSec: record.TlsaRecordsResponse.DnsSecStatus,
          Tlsa: tlsa.IsUsable,
          Dane: tlsa.DaneResult,
          Details:
            testType === DNS_BASELINE_TEST && record.TlsaRecordsResponse.DnsSecStatus.Status === DnsSecStatus.Disabled
              ? ""
              : record.TlsaRecordsResponse.DnsSecStatus.Status === DnsSecStatus.Success && testType !== DNS_BASELINE_TEST
              ? ComponentDecorators.replaceLinks(tlsa.DaneResult.Details)
              : ComponentDecorators.replaceLinks(record.TlsaRecordsResponse.DnsSecStatus.Details),
        });
      });
    }

    setItems(items);
  }, [record]);
  const showSummaries = false; // set to true to see per MX record summaries
  const preferenceStr = record.Preference !== -1 ? String(record.Preference) : "";
  const domainTitle = preferenceStr.concat(" ", record.Domain);
  const fullyCollapsed = state === AnimationState.COLLAPSED;
  const expandCollapseButtonTitle = (fullyCollapsed ? ClientStrings.ExpandNode : ClientStrings.CollapseNode).concat(" ", domainTitle);
  return (
    <Stack className={classNames.wrapper}>
      <div className={classNames.domainTitleWrapper}>
        <CommandBarButton
          iconProps={{ iconName: fullyCollapsed ? "ChevronDownSmall" : "ChevronUpSmall" }}
          text={domainTitle}
          styles={commandBarButtonStyle}
          onClick={() => (fullyCollapsed ? show : hide)()}
          ariaLabel={expandCollapseButtonTitle}
          aria-expanded={!fullyCollapsed}
        />
        <DaneValidationResultTestLabels response={response} record={record} testType={testType} domainDnsSecResult={null} />
        {showSummaries
          ? ComponentDecorators.replaceLinks(<Text variant="medium">{Helpers.getSummaryText(testType, response, record, null)}</Text>)
          : ""}
      </div>
      <div
        className={mergeStyles(
          classNames.resultTable,
          fullyCollapsed || state === AnimationState.COLLAPSING ? classNames.resultTableCollapsed : classNames.resultTableExpanded
        )}
      >
        {<DaneValidationResultTable items={items} testType={testType} />}
      </div>
    </Stack>
  );
};

export default DaneValidationResultMxEntity;
